/**
 * Classifies failures that happen while establishing an Auth0 session.
 *
 * The distinction that matters: a **dead cached session** (localStorage still holds a user + refresh
 * token, but Auth0 refuses to renew it — only a fresh login fixes that) versus **Auth0 being
 * unreachable** (a retry fixes that). Purging the cache on the second kind would sign people out
 * every time the network hiccups.
 *
 * Why this exists: `checkSession()` swallows renewal errors, so `useAuth0()` reports
 * `isAuthenticated: true` off the cached user even when every `getAccessTokenSilently()` call throws.
 * Without this classification the app has no way to tell "signed in" from "holding a corpse".
 */

/** Auth0 / OAuth error codes that mean the cached session is dead and cannot be silently renewed. */
const STALE_SESSION_CODES = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'mfa_required',
  'invalid_grant',
  'missing_refresh_token',
  'access_denied',
  'unauthorized',
]);

export type AuthFailure =
  /** The cached session can never produce a token again. Purge it and send the user back to login. */
  | { kind: 'stale-session'; code: string }
  /** Transient — Auth0 or the network is down. Keep the cache, let the user retry. */
  | { kind: 'unreachable'; message: string };

/** Auth0 SDK errors carry an OAuth code on `.error`; everything else (network, bugs) does not. */
function errorCode(err: unknown): string {
  if (typeof err !== 'object' || err === null || !('error' in err)) return '';
  const code = (err as { error: unknown }).error;
  return typeof code === 'string' ? code : '';
}

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  const code = errorCode(err);
  return code || 'Auth0 could not be reached.';
}

/**
 * Buckets a thrown Auth0 error into "the cached session is dead" vs "Auth0 is unreachable".
 *
 * Unknown errors deliberately fall on the `unreachable` side: keeping a session that might still be
 * good is recoverable, wrongly wiping a good one is not.
 */
export function classifyAuthError(err: unknown): AuthFailure {
  const code = errorCode(err);
  if (STALE_SESSION_CODES.has(code)) return { kind: 'stale-session', code };
  return { kind: 'unreachable', message: errorMessage(err) };
}

/**
 * One-shot marker for "we already purged a dead session and bounced the user through login this
 * browser session". Without it, a grant that keeps failing server-side turns the recovery into an
 * endless login redirect loop; with it, the second failure surfaces an error view instead.
 *
 * sessionStorage (not localStorage) so a new tab or a fresh browser session always gets one attempt.
 */
const RECOVERY_KEY = 'bevi-ailab.auth-recovery';

/** sessionStorage throws in some privacy modes — never let a storage failure break the auth path. */
function sessionStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export function recoveryAlreadyAttempted(): boolean {
  try {
    return sessionStore()?.getItem(RECOVERY_KEY) === 'true';
  } catch {
    return false;
  }
}

export function markRecoveryAttempt(): void {
  try {
    sessionStore()?.setItem(RECOVERY_KEY, 'true');
  } catch {
    /* storage unavailable — worst case we retry the recovery once more */
  }
}

export function clearRecoveryAttempt(): void {
  try {
    sessionStore()?.removeItem(RECOVERY_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}
